import { 
  doc, 
  getDoc, 
  getDocs,
  collection,
  Timestamp,
  writeBatch,
  runTransaction,
  deleteField
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { ClassMember } from '../types/alarm';
import { ClassMemberV2 } from '../types/index';

// ===== 배열 ↔ 중첩 객체 변환 유틸리티 =====

/**
 * 배열 형태의 members를 중첩 객체로 변환
 */
function membersArrayToObject(
  arr: ClassMember[]
): { [memberId: string]: ClassMemberV2 } {
  const obj: { [memberId: string]: ClassMemberV2 } = {};
  for (const member of arr) {
    const { id, ...rest } = member;
    obj[id] = {
      ...rest,
      createdAt: rest.createdAt || new Date(),
      updatedAt: rest.updatedAt || new Date()
    };
  }
  return obj;
}

/** itemCards raw(배열 또는 V2 객체) → 배열 (듀얼 읽기) */
function itemCardsRawToArray(raw: any): any[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') return Object.values(raw);
  return [];
}

/** itemCards 배열 → V2 객체 { [instanceId]: card } (저장용) */
function itemCardsArrayToObject(arr: any[]): Record<string, any> {
  const obj: Record<string, any> = {};
  for (const card of arr) {
    const key = card?.instanceId ?? card?.id ?? `_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    obj[key] = card;
  }
  return obj;
}

/**
 * 중첩 객체 형태의 members를 배열로 변환 (하위 호환성, itemCards 듀얼 읽기)
 */
function membersObjectToArray(
  obj: { [memberId: string]: ClassMemberV2 }
): ClassMember[] {
  return Object.entries(obj).map(([memberId, member]) => ({
    id: memberId,
    ...member,
    itemCards: itemCardsRawToArray(member.itemCards)
  }));
}

/**
 * 데이터가 배열인지 객체인지 확인
 */
function isArrayData(data: any): boolean {
  return Array.isArray(data);
}

/**
 * ClassMembers 컬렉션의 자동 분할 관리 서비스
 * - 문서 크기가 900KB를 넘으면 자동으로 새 파티션 생성
 * - ClassMembers-1, ClassMembers-2, ... 형태로 분할
 * - 기초 정보는 각 파티션에 유지, 누적 데이터(코인 히스토리, 아이템 카드)만 분산
 */
export class ClassMembersPartitionService {
  private userId: string;
  private classId: string;
  private memberGroup: string;
  private static readonly MAX_SIZE = 900000; // 900KB (여유를 두고 설정)
  private static readonly SIZE_ESTIMATE_FACTOR = 1.5; // JSON 크기 추정 계수

  constructor(userId: string, classId: string, memberGroup?: string) {
    this.userId = userId;
    this.classId = classId;

    // 그룹 파라미터가 명시되지 않은 경우(localStorage 기반) 현재 선택된 그룹을 사용
    // - 브라우저 환경에서만 동작하도록 가드
    let resolvedGroup = memberGroup;
    if (typeof resolvedGroup === 'undefined') {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          const key = `selected_member_group_${userId}_${classId}`;
          const saved = window.localStorage.getItem(key);
          if (saved) {
            resolvedGroup = saved;
          }
        }
      } catch {
        // ignore
      }
    }

    this.memberGroup = (resolvedGroup || 'A').toString().trim().toUpperCase();
  }

  /**
   * 그룹별 파티션 문서 ID
   * - A 그룹은 기존 데이터와의 호환을 위해 'ClassMembers-1' 형식을 유지
   * - B/C/... 그룹은 'ClassMembersB-1' 형식 사용
   */
  private getPartitionDocId(partitionNumber: number): string {
    if (this.memberGroup === 'A') {
      return `ClassMembers-${partitionNumber}`;
    }
    return `ClassMembers${this.memberGroup}-${partitionNumber}`;
  }

  /**
   * 현재 그룹에 해당하는 파티션 문서 ID인지 확인하고 파티션 번호를 반환
   */
  private parsePartitionNumber(docId: string): number | null {
    const regex =
      this.memberGroup === 'A'
        ? /^ClassMembers-(\d+)$/
        : new RegExp(`^ClassMembers${this.memberGroup}-(\\d+)$`);
    const match = docId.match(regex);
    if (!match) return null;
    return parseInt(match[1], 10);
  }

  /**
   * ClassMembers 컬렉션 경로
   */
  private getClassMembersCollectionPath(): string {
    const basePath = `users/${this.userId}/classes/${this.classId}`;
    if (this.memberGroup && this.memberGroup !== 'A') {
      return `${basePath}/ClassMembersProfile_${this.memberGroup}`;
    }
    return `${basePath}/ClassMembersProfile`;
  }

  /**
   * 특정 파티션 문서 경로
   */
  private getPartitionDocPath(partitionNumber: number): string {
    return `${this.getClassMembersCollectionPath()}/${this.getPartitionDocId(partitionNumber)}`;
  }

  /**
   * ClassMembersProfile에 저장할 멤버 객체 (coins/coinsHistory 제외)
   * 코인은 ClassMembersCoin에서 관리하므로 이 문서에는 저장하지 않음
   */
  private memberForStorage(m: any): any {
    const { coins, coinsHistory, ...rest } = m;
    // undefined 값을 가진 필드 제거 (Firestore는 undefined를 허용하지 않음)
    return Object.fromEntries(
      Object.entries(rest).filter(([_, value]) => value !== undefined)
    );
  }

  /**
   * 현재 활성 파티션 찾기
   * - totalSize 필드를 기준으로 가장 최신이면서 용량이 남은 파티션 반환
   * - 없으면 새로 생성
   */
  private async findActivePartition(): Promise<{
    partitionNumber: number;
    docRef: any;
    data: any;
  }> {
    const collectionPath = this.getClassMembersCollectionPath();
    const collectionRef = collection(db, collectionPath);
    const snapshot = await getDocs(collectionRef);

    let maxPartitionNumber = 0;
    let activePartition: any = null;

    // 모든 파티션 확인
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const partitionNumber = this.parsePartitionNumber(docSnap.id);
      if (partitionNumber !== null) {
        maxPartitionNumber = Math.max(maxPartitionNumber, partitionNumber);

        // 용량이 남아있는 파티션 찾기
        const currentSize = data.totalSize || 0;
        if (currentSize < ClassMembersPartitionService.MAX_SIZE) {
          if (!activePartition || partitionNumber > activePartition.partitionNumber) {
            activePartition = {
              partitionNumber,
              docRef: docSnap.ref,
              data
            };
          }
        }
      }
    }

    // 용량이 남은 파티션이 없으면 새 파티션 생성 (V2: 중첩 객체 형식)
    if (!activePartition) {
      const newPartitionNumber = maxPartitionNumber + 1;
      const newDocRef = doc(db, this.getPartitionDocPath(newPartitionNumber));
      
      return {
        partitionNumber: newPartitionNumber,
        docRef: newDocRef,
        data: {
          partitionNumber: newPartitionNumber,
          totalSize: 0,
          members: {}, // V2: 빈 객체로 시작
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        }
      };
    }

    return activePartition;
  }

  /**
   * 데이터 크기 추정
   * - JSON 문자열 길이를 기준으로 대략적인 크기 계산
   */
  private estimateSize(data: any): number {
    try {
      const jsonString = JSON.stringify(data);
      return jsonString.length * ClassMembersPartitionService.SIZE_ESTIMATE_FACTOR;
    } catch (error) {
      console.error('크기 추정 실패:', error);
      return 0;
    }
  }

  /**
   * 모든 파티션에서 멤버 정보 로드 (배열 형식으로 반환, 듀얼 지원)
   * - 모든 파티션의 members 배열/객체를 병합하여 반환
   * - 중복 제거 (최신 데이터 우선)
   * @alias loadMembers
   */
  async loadAllMembers(): Promise<ClassMember[]> {
    try {
      const collectionPath = this.getClassMembersCollectionPath();
      const collectionRef = collection(db, collectionPath);
      const snapshot = await getDocs(collectionRef);

      const memberMap = new Map<string, ClassMember>();

      // 파티션 번호순으로 정렬 (오래된 것부터 처리하고 최신 것으로 덮어쓰기)
      const docs = snapshot.docs
        .map((docSnap) => ({ docSnap, partitionNumber: this.parsePartitionNumber(docSnap.id) }))
        .filter((x) => x.partitionNumber !== null)
        .sort((a, b) => (a.partitionNumber as number) - (b.partitionNumber as number))
        .map((x) => x.docSnap);

      for (const docSnap of docs) {
        const data = docSnap.data();
        let members: ClassMember[] = [];

        // 배열/객체 듀얼 지원
        if (data.members) {
          if (isArrayData(data.members)) {
            // 기존 배열 형식
            members = data.members;
          } else {
            // V2 중첩 객체 형식 → 배열로 변환
            members = membersObjectToArray(data.members);
          }
        }

        // 각 멤버를 Map에 저장 (나중에 로드된 데이터가 덮어쓰기), itemCards 듀얼 읽기
        members.forEach((member: any) => {
          const id = member.id ?? (typeof member === 'object' ? undefined : member);
          const mid = id ?? (member as any).studentId;
          if (!mid) return;
          memberMap.set(mid, {
            ...member,
            id: mid,
            itemCards: itemCardsRawToArray(member.itemCards),
            createdAt: member.createdAt?.toDate?.() || new Date(member.createdAt) || new Date(),
            updatedAt: member.updatedAt?.toDate?.() || new Date(member.updatedAt) || new Date(),
            coinsHistory: member.coinsHistory?.map((history: any) => ({
              ...history,
              earnedAt: history.earnedAt?.toDate?.() || new Date(history.earnedAt) || new Date()
            })) || []
          });
        });
      }

      return Array.from(memberMap.values());
    } catch (error) {
      console.error('❌ ClassMembers 로드 실패:', error);
      throw error;
    }
  }

  /**
   * 모든 파티션에서 멤버 정보 로드 (V2: 중첩 객체 형식으로 반환)
   * - 모든 파티션의 members 객체를 병합하여 반환
   * - 중복 제거 (최신 데이터 우선)
   */
  async loadAllMembersV2(): Promise<{ [memberId: string]: ClassMemberV2 }> {
    try {
      const collectionPath = this.getClassMembersCollectionPath();
      const collectionRef = collection(db, collectionPath);
      const snapshot = await getDocs(collectionRef);

      const memberMap = new Map<string, ClassMemberV2>();

      // 파티션 번호순으로 정렬 (오래된 것부터 처리하고 최신 것으로 덮어쓰기)
      const docs = snapshot.docs
        .map((docSnap) => ({ docSnap, partitionNumber: this.parsePartitionNumber(docSnap.id) }))
        .filter((x) => x.partitionNumber !== null)
        .sort((a, b) => (a.partitionNumber as number) - (b.partitionNumber as number))
        .map((x) => x.docSnap);

      for (const docSnap of docs) {
        const data = docSnap.data();
        let membersObj: { [memberId: string]: ClassMemberV2 } = {};

        // 배열/객체 듀얼 지원
        if (data.members) {
          if (isArrayData(data.members)) {
            // 기존 배열 형식 → 객체로 변환
            membersObj = membersArrayToObject(data.members);
          } else {
            // V2 중첩 객체 형식
            membersObj = data.members;
          }
        }

        // 각 멤버를 Map에 저장 (나중에 로드된 데이터가 덮어쓰기)
        Object.entries(membersObj).forEach(([memberId, member]) => {
          memberMap.set(memberId, {
            ...member,
            createdAt: member.createdAt instanceof Date ? member.createdAt : 
                       (member.createdAt as any)?.toDate?.() || new Date(),
            updatedAt: member.updatedAt instanceof Date ? member.updatedAt : 
                       (member.updatedAt as any)?.toDate?.() || undefined
          });
        });
      }

      // Map을 객체로 변환
      const result: { [memberId: string]: ClassMemberV2 } = {};
      memberMap.forEach((member, memberId) => {
        result[memberId] = member;
      });

      return result;
    } catch (error) {
      console.error('❌ ClassMembers 로드 실패 (V2):', error);
      throw error;
    }
  }

  /**
   * 멤버 데이터 저장 (V2: 중첩 객체 형식, 자동 분할 포함)
   * - 각 멤버의 기초 정보는 모든 파티션에 유지
   * - 누적 데이터(코인 히스토리, 아이템)는 활성 파티션에만 추가
   */
  async saveMembers(members: ClassMember[]): Promise<void> {
    try {
      // 1. 현재 활성 파티션 찾기 (docRef/partitionNumber 확보)
      const { partitionNumber, docRef } = await this.findActivePartition();
      let savedSize: number | null = null;

      // 2. 트랜잭션으로 최신 데이터 기준 병합/저장
      await runTransaction(db, async (tx) => {
        const freshSnap = await tx.get(docRef);
        
        // 문서가 없으면 초기 데이터로 생성
        const currentData = freshSnap.exists() 
          ? (freshSnap.data() as any)
          : {
              partitionNumber,
              members: {},
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
              totalSize: 0
            };
        
        // 현재 members를 객체로 변환 (배열/객체 듀얼 지원)
        let currentMembersObj: { [memberId: string]: ClassMemberV2 } = {};
        if (currentData.members) {
          if (isArrayData(currentData.members)) {
            currentMembersObj = membersArrayToObject(currentData.members);
          } else {
            currentMembersObj = currentData.members;
          }
        }

        // 각 멤버별로 처리 (최신 데이터 기준 병합)
      for (const member of members) {
          const memberId = member.id;
          const existingMember = currentMembersObj[memberId];

          if (existingMember) {
            // 동시 업데이트 충돌 방지: 외부 변경 가능 필드는 최신값 유지 (undefined가 아닌 경우만)
            const preservedFields: Record<string, any> = {};
            if (existingMember.itemCards !== undefined) preservedFields.itemCards = existingMember.itemCards;
            if (existingMember.rewardedLevels !== undefined) preservedFields.rewardedLevels = existingMember.rewardedLevels;
            if (existingMember.pendingNotifications !== undefined) preservedFields.pendingNotifications = existingMember.pendingNotifications;
            if (existingMember.unlockedProfiles !== undefined) preservedFields.unlockedProfiles = existingMember.unlockedProfiles;
            if (existingMember.photoProfilePath !== undefined) preservedFields.photoProfilePath = existingMember.photoProfilePath;
            if (existingMember.photoProfileUrl !== undefined) preservedFields.photoProfileUrl = existingMember.photoProfileUrl;
            if (existingMember.photoProfileVersion !== undefined) preservedFields.photoProfileVersion = existingMember.photoProfileVersion;
            
            // 저장 시 coins/coinsHistory 제외 (ClassMembersCoin에서 관리)
            const { coins, coinsHistory, id, ...memberData } = member;
            currentMembersObj[memberId] = {
              ...existingMember,
              ...memberData,
              ...preservedFields,
              updatedAt: new Date()
          };
        } else {
            // 저장 시 coins/coinsHistory 제외 (ClassMembersCoin에서 관리)
            const { coins, coinsHistory, id, ...memberData } = member;
            currentMembersObj[memberId] = {
              ...memberData,
              createdAt: member.createdAt || new Date(),
              updatedAt: new Date()
            };
          }
        }

        // V2: 중첩 객체로 저장
      const newData = {
        ...currentData,
          members: currentMembersObj,
        updatedAt: Timestamp.now()
      };
      const estimatedSize = this.estimateSize(newData);
      newData.totalSize = estimatedSize;
        savedSize = estimatedSize;

        // 용량 초과 시 새 파티션 생성
      if (estimatedSize > ClassMembersPartitionService.MAX_SIZE) {
        console.log(`⚠️ 파티션 용량 초과 (${estimatedSize} bytes), 새 파티션 생성`);
        
          // 기초 정보만 추출 (코인은 ClassMembersCoin에서 관리하므로 제외, 아이템 카드는 새 파티션에서 빈 배열)
          // undefined 값 제거
          const basicInfoMembersObj: { [memberId: string]: ClassMemberV2 } = {};
          Object.entries(currentMembersObj).forEach(([memberId, member]) => {
            const basicInfo: ClassMemberV2 = {
              number: member.number,
              name: member.name,
              accessCode: member.accessCode,
              itemCards: [],
              createdAt: member.createdAt || new Date(), // 필수 필드
              updatedAt: new Date()
            };
            if (member.profileImage !== undefined) basicInfo.profileImage = member.profileImage;
            if (member.photoProfilePath !== undefined) basicInfo.photoProfilePath = member.photoProfilePath;
            if (member.photoProfileUrl !== undefined) basicInfo.photoProfileUrl = member.photoProfileUrl;
            if (member.photoProfileVersion !== undefined) basicInfo.photoProfileVersion = member.photoProfileVersion;
            if (member.notes !== undefined) basicInfo.notes = member.notes;
            basicInfoMembersObj[memberId] = basicInfo;
          });

        const newPartitionNumber = partitionNumber + 1;
        const newDocRef = doc(db, this.getPartitionDocPath(newPartitionNumber));
        const newPartitionData = {
          partitionNumber: newPartitionNumber,
            totalSize: this.estimateSize({ members: basicInfoMembersObj }),
            members: basicInfoMembersObj,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };

          // 새 파티션 생성 + 기존 파티션 읽기 전용 마킹을 트랜잭션으로 보장
          tx.set(newDocRef, newPartitionData);
          tx.set(docRef, {
          ...newData,
          readonly: true,
          totalSize: estimatedSize
        });
      } else {
          // 일반 저장 (V2: 중첩 객체)
          tx.set(docRef, newData);
      }
      });

      console.log(`✅ ClassMembers 저장 완료 (V2, 파티션: ${partitionNumber}, 크기: ${savedSize ?? 'N/A'} bytes)`);
    } catch (error) {
      console.error('❌ ClassMembers 저장 실패:', error);
      throw error;
    }
  }

  /**
   * 단일 멤버 정보 저장
   */
  async saveMember(member: ClassMember): Promise<void> {
    const allMembers = await this.loadAllMembers();
    const memberIndex = allMembers.findIndex(m => m.id === member.id);

    if (memberIndex >= 0) {
      allMembers[memberIndex] = member;
    } else {
      allMembers.push(member);
    }

    await this.saveMembers(allMembers);
  }

  /**
   * 멤버 삭제 (V2: deleteField() 사용 - 부분 삭제)
   * Firestore 트랜잭션의 tx.get()은 DocumentReference만 받으므로,
   * getDocs로 문서 ref 목록을 얻은 뒤, 트랜잭션 내에서 각 docRef로 tx.get(docRef) 후 업데이트
   */
  async deleteMember(memberId: string): Promise<void> {
    try {
      const collectionPath = this.getClassMembersCollectionPath();
      const collectionRef = collection(db, collectionPath);
      const listSnapshot = await getDocs(collectionRef);
      const docRefs = listSnapshot.docs
        .filter((docSnap) => this.parsePartitionNumber(docSnap.id) !== null)
        .map((docSnap) => docSnap.ref);

      if (docRefs.length === 0) {
        console.log(`✅ 멤버 삭제 완료 (파티션 없음): ${memberId}`);
        return;
      }

      await runTransaction(db, async (tx) => {
        for (const docRef of docRefs) {
          const docSnap = await tx.get(docRef);
          if (!docSnap.exists()) continue;
        const data = docSnap.data();
          
          // 배열/객체 듀얼 지원
          let hasMember = false;
          if (data.members) {
            if (isArrayData(data.members)) {
              hasMember = data.members.some((m: any) => m.id === memberId);
            } else {
              hasMember = memberId in data.members;
            }
          }

          if (hasMember) {
            // V2: deleteField()를 사용하여 해당 멤버만 삭제
            const updateData: any = {
              [`members.${memberId}`]: deleteField(),
            updatedAt: Timestamp.now()
          };
            
            // 크기 재계산을 위해 전체 데이터 읽기
            let membersObj: { [memberId: string]: ClassMemberV2 } = {};
            if (data.members) {
              if (isArrayData(data.members)) {
                membersObj = membersArrayToObject(data.members);
              } else {
                membersObj = data.members;
              }
            }
            delete membersObj[memberId];
            
            updateData.totalSize = this.estimateSize({ ...data, members: membersObj });
            tx.update(docRef, updateData);
          }
        }
      });

      console.log(`✅ 멤버 삭제 완료 (V2): ${memberId}`);
    } catch (error) {
      console.error('❌ 멤버 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 모든 파티션에서 멤버 정보 로드 (배열 형식으로 반환, 듀얼 지원)
   * - loadAllMembers의 별칭
   */
  async loadMembers(): Promise<ClassMember[]> {
    return this.loadAllMembers();
  }

  /**
   * 특정 멤버 조회
   */
  async getMember(memberId: string): Promise<ClassMember | null> {
    const allMembers = await this.loadAllMembers();
    return allMembers.find(m => m.id === memberId) || null;
  }

  /**
   * 특정 멤버 업데이트 (V2: dot notation 부분 업데이트)
   * - 멤버의 특정 필드만 업데이트
   * - 부분 업데이트를 지원하여 효율적
   * 
   * @param memberId 업데이트할 멤버 ID
   * @param updates 업데이트할 필드들 (Partial<ClassMemberV2>)
   * 
   * @example
   * ```typescript
   * await updateMember('member123', {
   *   name: '새 이름',
   *   notes: '메모 업데이트'
   * });
   * ```
   */
  async updateMember(
    memberId: string,
    updates: Partial<ClassMemberV2>
  ): Promise<void> {
    try {
      console.log(`🔄 멤버 ${memberId} 업데이트 시작 (V2)`);
      
      const collectionRef = collection(db, this.getClassMembersCollectionPath());
      const snapshot = await getDocs(collectionRef);

      for (const docSnapshot of snapshot.docs) {
        if (this.parsePartitionNumber(docSnapshot.id) === null) continue;
        const data = docSnapshot.data();
        
        // 배열/객체 듀얼 지원
        let hasMember = false;
        if (data.members) {
          if (isArrayData(data.members)) {
            hasMember = data.members.some((m: any) => m.id === memberId);
          } else {
            hasMember = memberId in data.members;
          }
        }

        if (hasMember) {
          await runTransaction(db, async (tx) => {
            const freshSnap = await tx.get(docSnapshot.ref);
            if (!freshSnap.exists()) {
              throw new Error('문서를 찾을 수 없습니다.');
            }
            const freshData = freshSnap.data() as any;
            
            // 현재 멤버 데이터 가져오기 (배열/객체 듀얼 지원)
            let currentMember: any = null;
            if (freshData.members) {
              if (isArrayData(freshData.members)) {
                const member = freshData.members.find((m: any) => m.id === memberId);
                if (member) {
                  const { id, ...rest } = member;
                  currentMember = rest;
                }
              } else {
                currentMember = freshData.members[memberId];
              }
            }
            
            if (!currentMember) {
              throw new Error('멤버를 찾을 수 없습니다.');
            }

            // V2: dot notation으로 부분 업데이트
            const updateObj: { [key: string]: any } = {
              [`members.${memberId}.updatedAt`]: new Date(),
              updatedAt: Timestamp.now()
            };

            // 업데이트할 필드들을 dot notation으로 추가
            Object.keys(updates).forEach(key => {
              const value = (updates as any)[key];
              if (value !== undefined) {
                updateObj[`members.${memberId}.${key}`] = value;
              }
            });

            // 크기 재계산을 위해 전체 데이터 읽기
            let membersObj: { [memberId: string]: ClassMemberV2 } = {};
            if (freshData.members) {
              if (isArrayData(freshData.members)) {
                membersObj = membersArrayToObject(freshData.members);
              } else {
                membersObj = { ...freshData.members };
              }
            }
            membersObj[memberId] = {
              ...membersObj[memberId],
              ...updates,
              updatedAt: new Date()
            };
            
            updateObj.totalSize = this.estimateSize({ ...freshData, members: membersObj });

            tx.update(docSnapshot.ref, updateObj);
          });

          console.log(`✅ 멤버 ${memberId} 업데이트 완료 (V2)`);

          try {
            const { userDataService } = await import('../services/userDataService');
            userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);
          } catch (error) {
            console.warn('멤버 업데이트 후 캐시 무효화 실패 (무시):', error);
          }

          return;
        }
      }

      console.log(`❌ 멤버 ${memberId}을 찾을 수 없습니다`);
      throw new Error(`멤버 ${memberId}을 찾을 수 없습니다`);
    } catch (error) {
      console.error('❌ 멤버 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 기존 단일 문서 마이그레이션
   * - 기존 class_{classId} 형태의 문서를 ClassMembers-1로 마이그레이션
   */
  async migrateFromLegacyFormat(): Promise<void> {
    try {
      // 기존 문서 경로
      const legacyDocPath = `users/${this.userId}/classes/${this.classId}/ClassMembers/${this.classId}`;
      const legacyDocRef = doc(db, legacyDocPath);
      const legacyDoc = await getDoc(legacyDocRef);

      if (!legacyDoc.exists()) {
        console.log('마이그레이션할 레거시 문서가 없습니다.');
        return;
      }

      const legacyData = legacyDoc.data();
      const members = legacyData.members || [];

      console.log(`🔄 마이그레이션 시작: ${members.length}명의 멤버 데이터`);

      // ClassMembers-1로 저장
      await this.saveMembers(members);

      console.log('✅ 마이그레이션 완료');
      console.log('⚠️ 레거시 문서는 수동으로 삭제하거나 백업용으로 유지할 수 있습니다.');
    } catch (error) {
      console.error('❌ 마이그레이션 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 아이템 카드 목록 업데이트
   */
  async updateStudentItemCards(studentId: string, itemCards: any[]): Promise<void> {
    try {
      await this.updateStudentItemCardsAtomic(studentId, itemCards);
    } catch (error) {
      console.error('❌ 멤버 아이템 카드 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버 데이터 트랜잭션 업데이트 (V2: dot notation 부분 업데이트, 동시 업데이트 보호)
   */
  private async updateMemberWithTransaction(
    studentId: string,
    updater: (member: any) => any,
    logLabel: string
  ): Promise<void> {
      const collectionRef = collection(db, this.getClassMembersCollectionPath());
      const snapshot = await getDocs(collectionRef);
      
      for (const docSnapshot of snapshot.docs) {
      if (this.parsePartitionNumber(docSnapshot.id) === null) continue;
        const data = docSnapshot.data();
      
      // 배열/객체 듀얼 지원
      let hasMember = false;
      if (data.members) {
        if (isArrayData(data.members)) {
          hasMember = data.members.some((m: any) => m.id === studentId);
        } else {
          hasMember = studentId in data.members;
        }
      }

      if (hasMember) {
        await runTransaction(db, async (tx) => {
          const freshSnap = await tx.get(docSnapshot.ref);
          if (!freshSnap.exists()) {
            throw new Error('문서를 찾을 수 없습니다.');
          }
          const freshData = freshSnap.data() as any;
          
          // 현재 멤버 데이터 가져오기 (배열/객체 듀얼 지원)
          let currentMember: any = null;
          if (freshData.members) {
            if (isArrayData(freshData.members)) {
              const member = freshData.members.find((m: any) => m.id === studentId);
              if (member) {
                const { id, ...rest } = member;
                currentMember = rest;
              }
            } else {
              currentMember = freshData.members[studentId];
            }
          }
          
          if (!currentMember) {
            throw new Error('멤버를 찾을 수 없습니다.');
          }

          const nextMember = updater({ ...currentMember });
          if (!nextMember || typeof nextMember !== 'object') {
            throw new Error('업데이트할 멤버 데이터가 올바르지 않습니다.');
          }

          // V2: dot notation으로 부분 업데이트
          const updateObj: { [key: string]: any } = {
            [`members.${studentId}.updatedAt`]: new Date(),
            updatedAt: Timestamp.now()
          };

          // 업데이트할 필드들을 dot notation으로 추가 (itemCards는 V2 객체로 저장)
          Object.keys(nextMember).forEach(key => {
            if (nextMember[key] === undefined) return;
            const val = key === 'itemCards' && Array.isArray(nextMember[key])
              ? itemCardsArrayToObject(nextMember[key])
              : nextMember[key];
            updateObj[`members.${studentId}.${key}`] = val;
          });

          // 크기 재계산을 위해 전체 데이터 읽기
          let membersObj: { [memberId: string]: ClassMemberV2 } = {};
          if (freshData.members) {
            if (isArrayData(freshData.members)) {
              membersObj = membersArrayToObject(freshData.members);
            } else {
              membersObj = { ...freshData.members };
            }
          }
          membersObj[studentId] = {
            ...membersObj[studentId],
            ...nextMember,
            updatedAt: new Date()
          };
          
          updateObj.totalSize = this.estimateSize({ ...freshData, members: membersObj });

          tx.update(docSnapshot.ref, updateObj);
        });

          try {
            const { userDataService } = await import('../services/userDataService');
            userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);
          } catch (error) {
          console.warn(`${logLabel} 업데이트 후 캐시 무효화 실패 (무시):`, error);
          }

          return;
        }
      }
      
    console.log(`❌ 멤버 ${studentId}을 찾을 수 없습니다 (${logLabel} 업데이트 실패)`);
    throw new Error(`멤버 ${studentId}을 찾을 수 없습니다`);
  }

  /**
   * 특정 멤버의 아이템 카드 목록 업데이트 (트랜잭션)
   */
  async updateStudentItemCardsAtomic(
    studentId: string,
    itemCardsOrUpdater: any[] | ((current: any[]) => any[])
  ): Promise<void> {
    try {
      await this.updateMemberWithTransaction(
        studentId,
        (member: any) => {
          const current = itemCardsRawToArray(member.itemCards);
          const next =
            typeof itemCardsOrUpdater === 'function'
              ? itemCardsOrUpdater(current)
              : itemCardsOrUpdater;

          if (!Array.isArray(next)) {
            throw new Error('itemCards 업데이트 결과가 배열이 아닙니다.');
          }

          return { itemCards: next };
        },
        'itemCards'
      );
    } catch (error) {
      console.error('❌ 멤버 아이템 카드 업데이트 실패 (atomic):', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 잠금 해제 프로필 추가 (트랜잭션)
   */
  async addStudentUnlockedProfileAtomic(studentId: string, profileId: string): Promise<void> {
    try {
      await this.updateMemberWithTransaction(
        studentId,
        (member: any) => {
          const current = Array.isArray(member.unlockedProfiles) ? member.unlockedProfiles : [];
          if (current.includes(profileId)) {
            return { unlockedProfiles: current };
          }
          return { unlockedProfiles: [...current, profileId] };
        },
        'unlockedProfiles'
      );
    } catch (error) {
      console.error('❌ unlockedProfiles 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 알림 추가 (트랜잭션)
   */
  async addStudentPendingNotificationAtomic(studentId: string, notification: any): Promise<void> {
    try {
      await this.updateMemberWithTransaction(
        studentId,
        (member: any) => {
          const current = Array.isArray(member.pendingNotifications) ? member.pendingNotifications : [];
          return { pendingNotifications: [...current, notification] };
        },
        'pendingNotifications'
      );
    } catch (error) {
      console.error('❌ pendingNotifications 추가 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 알림 제거 (트랜잭션)
   */
  async removeStudentPendingNotificationAtomic(studentId: string, notificationId: string): Promise<void> {
    try {
      await this.updateMemberWithTransaction(
        studentId,
        (member: any) => {
          const current = Array.isArray(member.pendingNotifications) ? member.pendingNotifications : [];
          const next = current.filter((n: any) => n?.id !== notificationId);
          return { pendingNotifications: next };
        },
        'pendingNotifications'
      );
    } catch (error) {
      console.error('❌ pendingNotifications 제거 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 rewardedLevels 추가 (트랜잭션)
   */
  async addStudentRewardedLevelsAtomic(studentId: string, levels: number[]): Promise<void> {
    try {
      await this.updateMemberWithTransaction(
        studentId,
        (member: any) => {
          const current = Array.isArray(member.rewardedLevels) ? member.rewardedLevels : [];
          const next = Array.from(new Set([...current, ...levels])).sort((a, b) => a - b);
          return { rewardedLevels: next };
        },
        'rewardedLevels'
      );
    } catch (error) {
      console.error('❌ rewardedLevels 추가 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 rewardedLevels 제거 (트랜잭션)
   */
  async removeStudentRewardedLevelsAtomic(studentId: string, levelsToRemove: number[]): Promise<void> {
    try {
      await this.updateMemberWithTransaction(
        studentId,
        (member: any) => {
          const current = Array.isArray(member.rewardedLevels) ? member.rewardedLevels : [];
          const next = current.filter((lvl: number) => !levelsToRemove.includes(lvl));
          return { rewardedLevels: next };
        },
        'rewardedLevels'
      );
    } catch (error) {
      console.error('❌ rewardedLevels 제거 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버에게 아이템 카드 1장 추가 (V2: dot notation 부분 업데이트, 동시 구매 시 덮어쓰기 방지)
   * - 트랜잭션으로 최신 문서를 읽고 해당 멤버의 itemCards만 append
   */
  async addStudentItemCardAtomic(studentId: string, newItemCard: any): Promise<void> {
    try {
      console.log(`🧩 멤버 ${studentId} 아이템 카드 추가 시작 (atomic, V2)`);

      const collectionRef = collection(db, this.getClassMembersCollectionPath());
      const snapshot = await getDocs(collectionRef);

      for (const docSnapshot of snapshot.docs) {
        if (this.parsePartitionNumber(docSnapshot.id) === null) continue;
        const data = docSnapshot.data();
        
        // 배열/객체 듀얼 지원
        let hasMember = false;
        if (data.members) {
          if (isArrayData(data.members)) {
            hasMember = data.members.some((m: any) => m.id === studentId);
          } else {
            hasMember = studentId in data.members;
          }
        }
        
        if (hasMember) {
          await runTransaction(db, async (tx) => {
            const freshSnap = await tx.get(docSnapshot.ref);
            if (!freshSnap.exists()) {
              throw new Error('문서를 찾을 수 없습니다.');
            }
            const freshData = freshSnap.data() as any;
            
            // 현재 멤버 데이터 가져오기 (배열/객체 듀얼 지원)
            let currentMember: any = null;
            if (freshData.members) {
              if (isArrayData(freshData.members)) {
                const member = freshData.members.find((m: any) => m.id === studentId);
                if (member) {
                  const { id, ...rest } = member;
                  currentMember = rest;
                }
              } else {
                currentMember = freshData.members[studentId];
              }
            }
            
            if (!currentMember) {
              throw new Error('멤버를 찾을 수 없습니다.');
            }

            const currentCards = itemCardsRawToArray(currentMember.itemCards);
            const updatedCards = [...currentCards, newItemCard];
            const itemCardsPayload = itemCardsArrayToObject(updatedCards);
            const updateObj: { [key: string]: any } = {
              [`members.${studentId}.itemCards`]: itemCardsPayload,
              [`members.${studentId}.updatedAt`]: new Date(),
              updatedAt: Timestamp.now()
            };
            
            // 크기 재계산을 위해 전체 데이터 읽기
            let membersObj: { [memberId: string]: ClassMemberV2 } = {};
            if (freshData.members) {
              if (isArrayData(freshData.members)) {
                membersObj = membersArrayToObject(freshData.members);
              } else {
                membersObj = { ...freshData.members };
              }
            }
            (membersObj as any)[studentId] = {
              ...membersObj[studentId],
              itemCards: itemCardsPayload,
              updatedAt: new Date()
            };
            updateObj.totalSize = this.estimateSize({ ...freshData, members: membersObj });
            tx.update(docSnapshot.ref, updateObj);
          });
          console.log(`✅ 멤버 ${studentId} 아이템 카드 추가 완료 (문서: ${docSnapshot.id}, V2)`);

          try {
            const { userDataService } = await import('../services/userDataService');
            userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);
            console.log('🗑️ 아이템 카드 추가 후 캐시 무효화 완료');
          } catch (error) {
            console.warn('아이템 카드 추가 후 캐시 무효화 실패 (무시):', error);
          }

          return;
        }
      }

      console.log(`❌ 멤버 ${studentId}을 찾을 수 없습니다`);
    } catch (error) {
      console.error('❌ 멤버 아이템 카드 추가 실패:', error);
      throw error;
    }
  }

  /**
   * 번호와 이름으로 멤버 찾기 (모든 파티션 검색)
   */
  async getMemberByNumberAndName(studentNumber: string, studentName: string): Promise<ClassMember> {
    try {
      const collectionPath = this.getClassMembersCollectionPath();
      console.log(`🔍 멤버 검색 시작: 번호=${studentNumber}, 이름=${studentName}, 경로=${collectionPath}`);

      // 모든 파티션 문서 조회
      const collectionRef = collection(db, collectionPath);
      console.log(`📋 컬렉션 쿼리 시작: ${collectionPath}`);
      const snapshot = await getDocs(collectionRef);
      console.log(`📋 컬렉션 쿼리 완료: ${snapshot.docs.length}개 문서 발견`);

      // 컬렉션이 비어있으면 해당 그룹에 멤버가 없음
      if (snapshot.docs.length === 0) {
        console.log(`📋 컬렉션이 비어있음: ${collectionPath} (해당 그룹에 멤버 없음)`);
        throw new Error(`멤버를 찾을 수 없습니다: ${studentNumber} - ${studentName}`);
      }

      for (const docSnapshot of snapshot.docs) {
        if (this.parsePartitionNumber(docSnapshot.id) === null) continue; // 다른 그룹/비파티션 문서 스킵
        const data = docSnapshot.data();
        
        // 배열/객체 듀얼 지원
        let members: ClassMember[] = [];
        if (data.members) {
          if (isArrayData(data.members)) {
            members = data.members;
          } else {
            members = membersObjectToArray(data.members);
          }
        }

        const matched = members.find((member: any) => member.number === studentNumber && member.name === studentName);
        if (matched) {
          console.log(`✅ 멤버 찾음: ${matched.name} (${matched.number})`);
          return matched as ClassMember;
        }
      }

      throw new Error(`멤버를 찾을 수 없습니다: ${studentNumber} - ${studentName}`);
    } catch (error) {
      console.error('❌ 멤버 검색 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 레벨업 보상 지급 완료 레벨 목록(rewardedLevels) 업데이트
   */
  async updateStudentRewardedLevels(studentId: string, rewardedLevels: number[]): Promise<void> {
    try {
      console.log(`🔄 멤버 ${studentId} rewardedLevels 업데이트 시작`);
          const deduped = Array.from(new Set(rewardedLevels)).sort((a, b) => a - b);
      await this.updateMemberWithTransaction(
        studentId,
        () => ({ rewardedLevels: deduped }),
        'rewardedLevels'
      );
      console.log(`✅ 멤버 ${studentId} rewardedLevels 업데이트 완료`, deduped);
          } catch (error) {
      console.error('❌ rewardedLevels 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 현재 레벨(currentLevel) 업데이트
   */
  async updateStudentCurrentLevel(studentId: string, currentLevel: number): Promise<void> {
    try {
      console.log(`🔄 멤버 ${studentId} currentLevel 업데이트 시작: ${currentLevel}`);
      await this.updateMemberWithTransaction(
        studentId,
        () => ({ currentLevel }),
        'currentLevel'
      );
      console.log(`✅ 멤버 ${studentId} currentLevel 업데이트 완료: ${currentLevel}`);
    } catch (error) {
      console.error('❌ currentLevel 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 프로필 이미지 업데이트 (V2: dot notation 부분 업데이트)
   * - 프로필 이미지는 기초 정보이므로 모든 파티션에 업데이트해야 함
   */
  async updateProfileImage(studentId: string, profileImageId: string): Promise<void> {
    try {
      console.log(`🖼️ 멤버 ${studentId} 프로필 이미지 업데이트 시작: ${profileImageId}`);
      
      // 모든 파티션 문서 조회
      const collectionRef = collection(db, this.getClassMembersCollectionPath());
      const snapshot = await getDocs(collectionRef);
      
      const batch = writeBatch(db);
      let updated = false;
      
      // 모든 파티션에서 해당 멤버의 프로필 이미지 업데이트
      for (const docSnapshot of snapshot.docs) {
        if (this.parsePartitionNumber(docSnapshot.id) === null) continue; // 다른 그룹/비파티션 문서 스킵
        const data = docSnapshot.data();
        
        // 배열/객체 듀얼 지원
        let hasMember = false;
        if (data.members) {
          if (isArrayData(data.members)) {
            hasMember = data.members.some((m: any) => m.id === studentId);
          } else {
            hasMember = studentId in data.members;
          }
        }
        
        if (hasMember) {
          // V2: dot notation으로 부분 업데이트
          batch.update(docSnapshot.ref, {
            [`members.${studentId}.profileImage`]: profileImageId,
            [`members.${studentId}.updatedAt`]: new Date(),
            updatedAt: Timestamp.now()
          });
          
          updated = true;
          console.log(`✅ 멤버 ${studentId} 프로필 이미지 업데이트 (문서: ${docSnapshot.id})`);
        }
      }
      
      if (updated) {
        await batch.commit();
        console.log(`✅ 멤버 ${studentId} 프로필 이미지 업데이트 완료 (모든 파티션, V2)`);
      } else {
        console.log(`❌ 멤버 ${studentId}을 찾을 수 없습니다`);
        throw new Error(`멤버 ${studentId}을 찾을 수 없습니다`);
      }
    } catch (error) {
      console.error('❌ 멤버 프로필 이미지 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 사진 프로필 필드 업데이트 (모든 파티션 반영)
   */
  async updatePhotoProfile(
    studentId: string,
    photoProfilePath?: string,
    photoProfileUrl?: string,
    photoProfileVersion?: number
  ): Promise<void> {
    try {
      const collectionRef = collection(db, this.getClassMembersCollectionPath());
      const snapshot = await getDocs(collectionRef);

      const batch = writeBatch(db);
      let updated = false;

      for (const docSnapshot of snapshot.docs) {
        if (this.parsePartitionNumber(docSnapshot.id) === null) continue;
        const data = docSnapshot.data();

        let hasMember = false;
        if (data.members) {
          if (isArrayData(data.members)) {
            hasMember = data.members.some((m: any) => m.id === studentId);
          } else {
            hasMember = studentId in data.members;
          }
        }

        if (hasMember) {
          batch.update(docSnapshot.ref, {
            [`members.${studentId}.photoProfilePath`]: photoProfilePath ?? deleteField(),
            [`members.${studentId}.photoProfileUrl`]: photoProfileUrl ?? deleteField(),
            [`members.${studentId}.photoProfileVersion`]: photoProfileVersion ?? deleteField(),
            [`members.${studentId}.updatedAt`]: new Date(),
            updatedAt: Timestamp.now()
          });
          updated = true;
        }
      }

      if (updated) {
        await batch.commit();
      } else {
        throw new Error(`멤버 ${studentId}을 찾을 수 없습니다`);
      }
    } catch (error) {
      console.error('❌ 멤버 사진 프로필 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 입장코드(accessCode) 업데이트 (V2: dot notation 부분 업데이트)
   * - 입장코드는 기초 정보이므로 모든 파티션에 업데이트해야 함
   */
  async updateAccessCode(studentId: string, accessCode: string): Promise<void> {
    try {
      console.log(`🔑 멤버 ${studentId} 입장코드 업데이트 시작`);
      
      // 모든 파티션 문서 조회
      const collectionRef = collection(db, this.getClassMembersCollectionPath());
      const snapshot = await getDocs(collectionRef);
      
      const batch = writeBatch(db);
      let updated = false;
      
      // 모든 파티션에서 해당 멤버의 입장코드 업데이트
      for (const docSnapshot of snapshot.docs) {
        if (this.parsePartitionNumber(docSnapshot.id) === null) continue; // 다른 그룹/비파티션 문서 스킵
        const data = docSnapshot.data();
        
        // 배열/객체 듀얼 지원
        let hasMember = false;
        if (data.members) {
          if (isArrayData(data.members)) {
            hasMember = data.members.some((m: any) => m.id === studentId);
          } else {
            hasMember = studentId in data.members;
          }
        }
        
        if (hasMember) {
          // V2: dot notation으로 부분 업데이트
          batch.update(docSnapshot.ref, {
            [`members.${studentId}.accessCode`]: accessCode,
            [`members.${studentId}.updatedAt`]: new Date(),
            updatedAt: Timestamp.now()
          });
          
          updated = true;
          console.log(`✅ 멤버 ${studentId} 입장코드 업데이트 (문서: ${docSnapshot.id})`);
        }
      }
      
      if (updated) {
        await batch.commit();
        console.log(`✅ 멤버 ${studentId} 입장코드 업데이트 완료 (모든 파티션, V2)`);
      } else {
        console.log(`❌ 멤버 ${studentId}을 찾을 수 없습니다`);
        throw new Error(`멤버 ${studentId}을 찾을 수 없습니다`);
      }
    } catch (error) {
      console.error('❌ 멤버 입장코드 업데이트 실패:', error);
      throw error;
    }
  }
}

